import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { withRouter } from "../utils/router";
import { darken } from "@mui/material/styles";
import { makeStyles } from 'tss-react/mui';
import {
  Toolbar,
  Typography,
  Tooltip,
  Button,
  Select,
  IconButton,
  Grid,
  Tabs,
  Tab,
  InputBase,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";

const useToolbarStyles = makeStyles()((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: "100%",
    height: "auto",
    minWidth: "360px",
    minHeight: "60px",
    borderRadius: "0 0 10px 10px",
    [theme.breakpoints.up("md")]: {
      minHeight: "90px",
    },
  },
  buttons: {
    textAlign: "center",
    paddingRight: theme.spacing(1),
    [theme.breakpoints.up("sm")]: {
      textAlign: "right",
    },
  },
  colorDefault: {
    backgroundColor: theme.palette.primary.light,
  },
  conferenceButton: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: darken(theme.palette.primary.light, 0.25),
    },
  },
  title: {
    flex: "auto",
    color: "white",
    fontWeight: 300,
    textAlign: "center",
    [theme.breakpoints.up("sm")]: {
      textAlign: "left",
    },
  },
  // The group filter: a compact outlined dropdown in the title bar.
  groupFilterItem: {
    minWidth: 180,
    fontSize: 14.5,
    fontWeight: 500,
    textAlign: "left",
    color: theme.palette.primary.contrastText,
    backgroundColor: "rgba(255, 255, 255, 0.14)",
    borderRadius: 8,
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      paddingTop: 7,
      paddingBottom: 7,
      paddingLeft: 0,
    },
    "& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.6)",
      borderWidth: 1,
    },
    "& .MuiSelect-icon": {
      color: theme.palette.primary.contrastText,
    },
  },
  groupFilterIcon: {
    marginLeft: 10,
    marginRight: 8,
    fontSize: 19,
  },
  titleConference: {
    flex: "1 1 100%",
    fontWeight: 300,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: "center",
    [theme.breakpoints.up("sm")]: {
      textAlign: "left",
    },
  },
  menu: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  tabs: {
    "& .MuiTab-root": {
      textTransform: "none",
      fontSize: 14.5,
      fontWeight: 500,
      minHeight: 44,
    },
  },
  tabLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  // How many items wait behind a tab.
  tabCount: {
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
  },
  searchBar: {
    padding: theme.spacing(1.5, 2),
  },
  menuRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1),
  },
  menuTabs: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  menuSearch: {
    flex: "0 1 280px",
    minWidth: 200,
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flex: "1 1 100%",
      paddingLeft: theme.spacing(2),
    },
  },
  // Compact rounded field with a leading icon, like the SMS search.
  searchField: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    width: "100%",
    maxWidth: 360,
    padding: theme.spacing(0.25, 1, 0.25, 1.5),
    borderRadius: 20,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
    "&:focus-within": {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    "& input": {
      padding: theme.spacing(0.75, 0),
    },
  },
  searchClear: {
    color: theme.palette.text.secondary,
  },
  clearButton: {
    color: theme.palette.primary.contrastText,
    borderColor: theme.palette.primary.contrastText,
  },
  delete: {
    "& svg": {
      color: theme.palette.primary.contrastText,
    },
  },
}));

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

const EnhancedTableToolbar = (props) => {
  const { classes, cx } = useToolbarStyles();
  const {
    setSelected,
    numSelected,
    items,
    menu,
    searchFunc,
    title,
    tab,
    history,
    location,
    hasClear,
    clearFunction,
    setPage,
    handleDelete,
    createItemCallback,
    createItemTooltip,
    useGroupFilter,
    createGroupOptions,
    setFilterGroup,
    filterGroup,
    refreshDataOnSameTab,
  } = props;
  const [value, setValue] = useState(tab);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setValue(tab);
  }, [tab])

  const handleChange = (event, newValue) => {
    if (newValue !== "") {
      if (refreshDataOnSameTab && newValue === value) {
        refreshDataOnSameTab()
      }
      setValue(newValue);
      setPage(0);
      history.push(location.pathname.replace(/\/[^/]*$/, `/${newValue}`));
    }
  };

  const deleteAction = () => {
    handleDelete(typeof items === "object" && items.length > 0 ? items : [items]);
    setSelected([]);
  }

  const handleSearch = (e) => {
    const searchTerm = e.target.value;
    setSearchTerm(searchTerm);
    searchFunc(searchTerm)
  };

  const searchField = searchFunc ? (
    <div className={classes.searchField}>
      <SearchIcon fontSize="small" />
      <InputBase
        className={classes.searchInput}
        placeholder={/conference/i.test(title || "") ? "Search conferences" : `Search ${String(title || "").toLowerCase()}`.trim()}
        value={searchTerm}
        onChange={handleSearch}
        inputProps={{ "aria-label": "search" }}
      />
      {searchTerm !== "" && (
        <IconButton
          size="small"
          className={classes.searchClear}
          aria-label="clear search"
          onClick={() => handleSearch({ target: { value: "" } })}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  ) : null;

  return (
    <div>
      <Toolbar
        className={
          cx(classes.root, classes.colorDefault)
        }
      >
        <Grid container sx={{
          alignItems: "center"
        }}>
          <Grid
            // The title takes the room the buttons leave.
            size={{
              xs: 12,
              sm: "grow"
            }}>
            {(
              numSelected > 0 ? (
                <Typography
                  className={classes.title}
                  variant="h6"
                  component="div"
                  sx={{
                    color: "inherit"
                  }}
                >
                  {title} ({numSelected} selected)
                </Typography>
              ) : (
                <Typography
                  className={classes.title}
                  variant="h6"
                  id="tableTitle"
                  component="div"
                >
                  {title}
                </Typography>
              )
            )}
          </Grid>
          <Grid
            className={classes.buttons}
            size={{
              xs: 12,
              sm: "auto"
            }}>
            {numSelected > 0 ? (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="delete"
                  className={classes.delete}
                  onClick={deleteAction}
                  size="large">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            ) : null}

            {numSelected === 0 && hasClear ? (
              <Tooltip title="Clear Current Notifications">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  disableElevation
                  className={classes.clearButton}
                  onClick={clearFunction}
                >
                  Clear
                </Button>
              </Tooltip>
            ) : null}
            {createItemCallback ? (
              <Tooltip title={createItemTooltip}>
                <Button
                  variant="contained"
                  startIcon={<GroupAddIcon />}
                  className={classes.conferenceButton}
                  onClick={createItemCallback}
                >
                  Create
                </Button>
              </Tooltip>
            ) :
              useGroupFilter ? (
                <Select
                  id="selectGroupSelect"
                  size="small"
                  displayEmpty
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className={classes.groupFilterItem}
                  startAdornment={<GroupsIcon className={classes.groupFilterIcon} />}
                  slotProps={{ input: { "aria-label": "filter by group" } }}
                >
                  {createGroupOptions()}
                </Select>
              ) : (
                null
              )}
          </Grid>
        </Grid>
      </Toolbar>
      {menu ? (
        // The tabs, with the search (when the list has one) at the end of
        // the same line. On a narrow screen the search drops below the tabs.
        <div className={cx(classes.menu, classes.menuRow)}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            className={cx(classes.tabs, classes.menuTabs)}
          >
            {menu.map((item, index) => (
              <Tab
                disabled={item.linkId !== "" ? false : true}
                key={index}
                value={item.linkId}
                label={item.count > 0
                  ? (
                    <span className={classes.tabLabel}>
                      {item.title}
                      <span className={classes.tabCount}>{item.count > 99 ? "99+" : item.count}</span>
                    </span>
                  )
                  : item.title}
                {...a11yProps(0)}
              />
            ))}
          </Tabs>
          {searchFunc ? (
            <div className={classes.menuSearch}>{searchField}</div>
          ) : null}
        </div>
      ) : null}
      {searchFunc && !menu ? (
        <Grid className={classes.searchBar} size={12}>
          {searchField}
        </Grid>
      ) : null}
    </div>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  menu: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  title: PropTypes.string.isRequired,
  tab: PropTypes.string.isRequired,
  searchFunc: PropTypes.func,
  hasClear: PropTypes.bool.isRequired,
  clearFunction: PropTypes.func,
  createItemCallback: PropTypes.func,
  createItemTooltip: PropTypes.string,
  useGroupFilter: PropTypes.bool.isRequired,
  createGroupOptions: PropTypes.func,
  setFilterGroup: PropTypes.func,
  filterGroup: PropTypes.string,
  refreshDataOnSameTab: PropTypes.func,
};

export default withRouter(EnhancedTableToolbar);